import { useRef, useState } from 'react';
import type { BlockerFunction } from 'react-router';
import { useBeforeUnload, useBlocker } from 'react-router';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { useI18n } from '#/i18n/hooks/useI18n';
import { Modal } from '#/modals/Modal';
import { spacing } from '#/theme/theme';
import { hideModal } from '#/zustand-stores/modalStore';

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: ${spacing[24]};
`;

type Props = {
  shouldBlock?: BlockerFunction;
};

export function NavigationBlocker({ shouldBlock }: Props) {
  const { i18n } = useI18n();
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // In RSC we need a ref to let shouldBlock know that blocking already happened,
  // otherwise it blocks any navigation after the user left the page
  const didBlockRef = useRef(false);

  const shouldBlockOnce: BlockerFunction = (args) => {
    if (didBlockRef.current) return false;
    return shouldBlock ? shouldBlock(args) : true;
  };

  const blocker = useBlocker(shouldBlockOnce);
  useBeforeUnload((e: BeforeUnloadEvent) => {
    if (!didBlockRef.current) {
      e.preventDefault();
    }
  });

  if (blocker.state !== 'blocked') return null;

  const onCancel = () => {
    hideModal();
    blocker.reset();
    setModalSubmitting(false);
  };

  return (
    <Modal
      title={i18n(
        'Do you want to leave this page? All unsaved data will be lost.',
      )}
      onClose={onCancel}
      qaId="form-navigation-warning-modal"
    >
      <ButtonContainer>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            didBlockRef.current = true;
            blocker.proceed();
            setModalSubmitting(true);
          }}
          disabled={modalSubmitting}
          qaId="confirm-button"
        >
          {i18n('Leave Page')}
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          qaId="cancel-button"
        >
          {i18n('Cancel')}
        </Button>
      </ButtonContainer>
    </Modal>
  );
}
